import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { FieldConfig } from 'src/app/core/interfaces/field-config';
import { DynamicFormServiceService } from 'src/app/shared/services/dynamic-form-service.service';

import { Constants } from '../constants';
import { BroadcastService } from '../services/broadcast.service';
import { LandingRedirectService } from '../services/landing-redirect.service';

@Component({
    exportAs: 'dynamicForm',
    selector: 'app-dynamic-form',
    templateUrl: './dynamic-form.component.html',
    standalone: false
})
export class DynamicFormComponent implements OnInit, OnDestroy {

  @Input() fields: FieldConfig[] = [];
  @Input() loadFor: string;
  @Input() hasChildAOE: boolean;
  @Input() innerPage: boolean;

  // tslint:disable-next-line: no-output-native
  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  @Output() formValueChangesEvent: EventEmitter<any> = new EventEmitter<any>();

  form: UntypedFormGroup;
  subscriptionList: Array<Subscription>;

  get value() {
    return this.form;
  }
  constructor(
    private readonly fb: UntypedFormBuilder, private readonly landingService: LandingRedirectService,
    private broadCastService: BroadcastService,
    private dynamicService: DynamicFormServiceService) {
    this.subscriptionList = new Array<Subscription>();
  }

  ngOnInit() {
    this.form = this.createControl();
    this.subscriptionList.push(this.form.valueChanges
      .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(x => {
        if (this.form.dirty) {
          this.formValueChangesEvent.emit();
        }
      }));
    this.subscriptionList.push(this.broadCastService.subscribe().subscribe(message => {
      if (message.payload && Array.isArray(message.payload)) {
        const updateFields = this.fields.filter((x) => message.payload.indexOf(x.labelCode) > -1);
        if (message.type === 'SHOW_OPTIONS') {
          this.revealquestion(updateFields, 'SHOW');
        } else if (message.type === 'CLEAR_OPTIONS') {
          this.revealquestion(updateFields, 'CLEAR');
        } else if (message.type === 'SHOW_OPTIONS_WITHOUT_RESET') {
          this.revealquestion(updateFields, 'SHOW', 'NO_RESET');
        }
      }
    }));
  }


  ngOnDestroy() {
    this.subscriptionList.forEach(X => X.unsubscribe());
  }

  private processFieldReset(field: any, reset?: string) {
    if (this.dynamicService.isControlExist(this.form, field.labelCode) && typeof (reset) === 'undefined') {
      if (field.type === Constants.FIELD_TYPE_CHECKBOX) {
        (this.form.get(field.labelCode) as UntypedFormArray).reset();
      } else {
        this.form.get(field.labelCode).setValue(null);
      }
    }
  }

  private revealquestion(revealedFields: any, type: string, reset?: string) {
    for (const key of revealedFields) {
      let objIndex = -1;
      objIndex = this.fields.findIndex((obj => obj.labelCode === key.labelCode));
      this.fields[objIndex].isChildAoe = (type === 'SHOW') ? false : true;
      this.processFieldReset(this.fields[objIndex], reset);
      if (this.fields[objIndex].validations.length > 0 && type === 'SHOW' && this.form.get(this.fields[objIndex].labelCode)) {
        this.form.get(this.fields[objIndex].labelCode).setValidators(this.bindValidations(this.fields[objIndex]));
      } else if (this.fields[objIndex].validations.length > 0 && type !== 'SHOW') {
        if (this.form.get(this.fields[objIndex].labelCode)) {
          this.form.get(this.fields[objIndex].labelCode).clearValidators();
        }
        if (this.form.get(this.fields[objIndex].labelCode)) {
          this.form.get(this.fields[objIndex].labelCode).updateValueAndValidity();
        }
      }
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.form.valid) {
      this.submit.emit(this.form.value);
    } else {
      this.validateAllFormFields(this.form);
    }
  }

  private addCheckboxControl(field: any, group: UntypedFormGroup) {
    const control = field.options.map(c => new UntypedFormControl(c.isSelected || false,
      (!field.isChildAoe) ? this.bindValidations(field) : null));
    group.addControl(field.labelCode, new UntypedFormArray(control));
  }

  private resolveInitialValue(field: any): any {
    let initialValue = field.value;
    if ((field.type === 'RadioButton' || field.type === 'DropDown') && field.options) {
      const selectedOption = field.options.find(option => option.isSelected);
      if (selectedOption) {
        initialValue = selectedOption.code;
      }
    }
    return initialValue;
  }

  private applyComputedDisable(field: any, control: any) {
    if (field.isComputed || (field.labelCode === 'EDD' && !this.loadFor)) {
      const computedQuestionCodes = field.labelCode;
      if (this.landingService.computedQuestionCodes.indexOf(computedQuestionCodes) === -1 && computedQuestionCodes !== 'GADRAW') {
        this.landingService.computedQuestionCodes.push(computedQuestionCodes);
      }
      control.disable();
    }
  }

  private addNonCheckboxControl(field: any, group: UntypedFormGroup, disableFields: string[]) {
    if (field.labelCode !== 'GADRAWDAYS' || this.loadFor) {
      field.type = (field.isComputed || (this.loadFor && (field.labelCode === 'SONOID' ||
        field.labelCode === 'SSUPER'))) ? 'TextBox' : field.type;
      const initialValue = this.resolveInitialValue(field);
      const control = this.fb.control(
        initialValue,
        (!field.isChildAoe) ? this.bindValidations(field || []) : null
      );
      this.applyComputedDisable(field, control);
      if (disableFields.indexOf(field.labelCode) > -1 && !this.loadFor) { control.disable(); }
      group.addControl(field.labelCode, control);
    }
  }

  createControl() {
    const group = this.fb.group({});
    const disableFields = ['MWLBSKGS', 'YREQ'];
    this.fields.forEach(field => {
      if (field.type === 'CheckBox') {
        this.addCheckboxControl(field, group);
      } else {
        this.addNonCheckboxControl(field, group, disableFields);
      }
    });
    return group;
  }
  bindValidations(field: any) {
    let validList = [];
    const noValidation = ['MATWT'];
    switch (field.type) {
      case 'NumberBox':
        if ((field.inputType === 'Integer' || field.inputType === 'Float' || noValidation.indexOf(field.labelCode) > -1) &&
          (noValidation.indexOf(field.labelCode) === -1 || this.loadFor)) {
          if (field.validations !== null && field.validations.length > 0) {
            validList = [...validList, ...this.checkValidationsForValidators(field.validations, field.labelCode, field.inputType)];
          }
        }
        break;
      case 'TextBox':
      case 'DatePicker':
      case 'TimePicker':
      case 'DateTimePicker':
        if (field.validations !== null && field.validations.length > 0) {
          validList = [...validList, ...this.checkValidationsForValidators(field.validations)];
        }
        break;
      case 'DropDown':
        if (field.validations !== null && field.validations.length > 0) {
          validList = [...validList, ...this.checkValidationsForValidators(field.validations, field.type)];
        }
        break;
    }
    return Validators.compose(validList) || null;
  }

  checkValidationsForValidators(validations, type?, inputType?) {
    const validList = [];
    validations.forEach(valid => {
      switch (valid.name) {
        case 'rangeValidator':
          validations = valid.validator.split('-');
          validList.push(Validators.min(validations[0]));
          validList.push(Validators.max(validations[1]));
          break;
        case 'required':
          validList.push(Validators.required);
          break;
        case 'maxLength':
          if (type !== 'DropDown' && inputType !== 'Float') {
            validList.push(Validators.maxLength(valid.validator));
          }
          break;
        case 'pattern':
          if (type !== 'GAUS') {
            validList.push(Validators.pattern(valid.validator.replace(/\\\\/g, '\\')));
          }
          break;
        default:
          break;
      }
    });
    return validList;
  }

  validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

}




